import React, { useEffect, useState } from 'react';
import {
  Card, Table, Button, Tag, Modal, Form, Input, InputNumber, Select, Switch, DatePicker,
  Space, Popconfirm, message, Typography,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiClient } from 'Utils/api';

const { Title, Text } = Typography;

interface DiscountCode {
  id: string;
  code: string;
  type: 'DISCOUNT' | 'PROMO';
  value: number;
  valueType: 'PERCENT' | 'ABSOLUTE_CENTS';
  validFrom: string | null;
  validTo: string | null;
  isActive: boolean;
  usedAt: string | null;
  usedByOrderId: string | null;
  createdAt: string;
}

const formatValue = (c: DiscountCode): string => {
  if (c.valueType === 'PERCENT') return `${c.value} %`;
  return `${(c.value / 100).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €`;
};

const DiscountCodesPage: React.FC = () => {
  const [codes, setCodes] = useState<DiscountCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<DiscountCode | null>(null);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/admin/discount-codes');
      setCodes(res.data?.codes || []);
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ type: 'DISCOUNT', valueType: 'ABSOLUTE_CENTS', isActive: true });
    setModalOpen(true);
  };

  const openEdit = (c: DiscountCode) => {
    setEditing(c);
    form.setFieldsValue({
      code: c.code,
      type: c.type,
      valueType: c.valueType,
      value: c.valueType === 'ABSOLUTE_CENTS' ? c.value / 100 : c.value,
      isActive: c.isActive,
      validFrom: c.validFrom ? dayjs(c.validFrom) : null,
      validTo: c.validTo ? dayjs(c.validTo) : null,
    });
    setModalOpen(true);
  };

  const save = async () => {
    const values = await form.validateFields();
    const valueRaw = Number(values.value);
    const value = values.valueType === 'ABSOLUTE_CENTS' ? Math.round(valueRaw * 100) : Math.floor(valueRaw);

    try {
      if (editing) {
        const payload: any = {
          isActive: values.isActive,
          value,
          valueType: values.valueType,
        };
        if (values.type === 'PROMO') {
          payload.validFrom = values.validFrom ? values.validFrom.toISOString() : null;
          payload.validTo = values.validTo ? values.validTo.toISOString() : null;
        }
        await apiClient.patch(`/api/admin/discount-codes/${editing.id}`, payload);
        message.success('Code aktualisiert');
      } else {
        const payload: any = {
          code: values.code,
          type: values.type,
          value,
          valueType: values.valueType,
          isActive: values.isActive,
        };
        if (values.type === 'PROMO') {
          payload.validFrom = values.validFrom ? values.validFrom.toISOString() : null;
          payload.validTo = values.validTo ? values.validTo.toISOString() : null;
        }
        const res = await apiClient.post('/api/admin/discount-codes', payload);
        if (res.data?.status === 'error') {
          message.error(res.data.message);
          return;
        }
        message.success('Code angelegt');
      }
      setModalOpen(false);
      load();
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Fehler beim Speichern');
    }
  };

  const remove = async (id: string) => {
    try {
      await apiClient.delete(`/api/admin/discount-codes/${id}`);
      message.success('Gelöscht');
      load();
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Fehler beim Löschen');
    }
  };

  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
      render: (v: string) => <Text strong style={{ letterSpacing: 1 }}>{v}</Text>,
    },
    {
      title: 'Typ',
      dataIndex: 'type',
      render: (t: string) => (
        <Tag color={t === 'DISCOUNT' ? 'gold' : 'blue'}>
          {t === 'DISCOUNT' ? 'Rabattcode (einmalig)' : 'Aktionscode (mehrfach)'}
        </Tag>
      ),
    },
    {
      title: 'Wert',
      key: 'value',
      render: (_: any, r: DiscountCode) => formatValue(r),
    },
    {
      title: 'Gültigkeit',
      key: 'validity',
      render: (_: any, r: DiscountCode) => {
        if (r.type === 'DISCOUNT') return <Text type="secondary">—</Text>;
        const from = r.validFrom ? dayjs(r.validFrom).format('DD.MM.YYYY') : '…';
        const to = r.validTo ? dayjs(r.validTo).format('DD.MM.YYYY') : '…';
        return <span>{from} – {to}</span>;
      },
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: any, r: DiscountCode) => {
        if (!r.isActive) return <Tag>Inaktiv</Tag>;
        if (r.usedAt) return <Tag color="red">Verbraucht</Tag>;
        if (r.type === 'PROMO' && r.validTo && dayjs(r.validTo).isBefore(dayjs())) return <Tag color="orange">Abgelaufen</Tag>;
        return <Tag color="green">Aktiv</Tag>;
      },
    },
    {
      title: 'Verwendet von',
      dataIndex: 'usedByOrderId',
      render: (id: string | null) => id
        ? <a href={`/backend/orders/${id}`}>Bestellung öffnen</a>
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Aktionen',
      key: 'actions',
      render: (_: any, r: DiscountCode) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} data-testid={`edit-code-${r.code}`}>Bearbeiten</Button>
          <Popconfirm title="Wirklich löschen?" onConfirm={() => remove(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} data-testid={`delete-code-${r.code}`}>Löschen</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const watchType = Form.useWatch('type', form);
  const watchValueType = Form.useWatch('valueType', form);

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <Title level={3} style={{ margin: 0 }}>Rabattcodes</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} data-testid="create-discount-code-btn">
          Neuen Code anlegen
        </Button>
      </div>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        <b>Rabattcodes</b> sind einmalig einlösbar. <b>Aktionscodes</b> gelten mehrfach in einem festen Zeitraum.
        Pro Bestellung kann nur ein Code eingelöst werden.
      </Text>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns as any}
        dataSource={codes}
        pagination={{ pageSize: 20 }}
        data-testid="discount-codes-table"
      />

      <Modal
        title={editing ? `Code bearbeiten: ${editing.code}` : 'Neuen Code anlegen'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={save}
        okText="Speichern"
        cancelText="Abbrechen"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Code"
            name="code"
            rules={[{ required: !editing, message: 'Code erforderlich' }]}
          >
            <Input
              disabled={!!editing}
              placeholder="z. B. WELCOME50"
              maxLength={64}
              onChange={(e) => form.setFieldsValue({ code: e.target.value.toUpperCase().replace(/\s/g, '') })}
              data-testid="discount-code-input"
            />
          </Form.Item>

          <Form.Item label="Typ" name="type" rules={[{ required: true }]}>
            <Select disabled={!!editing} data-testid="discount-type-select"
              options={[
                { value: 'DISCOUNT', label: 'Rabattcode – einmalig einlösbar' },
                { value: 'PROMO', label: 'Aktionscode – mehrfach im Zeitraum' },
              ]}
            />
          </Form.Item>

          <Form.Item label="Wert" required style={{ marginBottom: 0 }}>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item name="value" rules={[{ required: true, message: 'Wert erforderlich' }]} noStyle>
                <InputNumber
                  min={watchValueType === 'PERCENT' ? 1 : 0.01}
                  max={watchValueType === 'PERCENT' ? 100 : undefined}
                  step={watchValueType === 'PERCENT' ? 1 : 0.01}
                  precision={watchValueType === 'PERCENT' ? 0 : 2}
                  style={{ width: '70%' }}
                  data-testid="discount-value-input"
                />
              </Form.Item>
              <Form.Item name="valueType" noStyle>
                <Select style={{ width: '30%' }} data-testid="discount-value-type-select"
                  options={[
                    { value: 'ABSOLUTE_CENTS', label: '€' },
                    { value: 'PERCENT', label: '%' },
                  ]}
                />
              </Form.Item>
            </Space.Compact>
          </Form.Item>

          {watchType === 'PROMO' && (
            <>
              <Form.Item label="Gültig von" name="validFrom" style={{ marginTop: 16 }}>
                <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
              </Form.Item>
              <Form.Item label="Gültig bis" name="validTo">
                <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
              </Form.Item>
            </>
          )}

          <Form.Item label="Aktiv" name="isActive" valuePropName="checked" style={{ marginTop: 16 }}>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default DiscountCodesPage;
